import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown } from "lucide-react";

const LINKS = [
  { to: "/experiences", label: "Experiences" },
  { to: "/games", label: "VR Games" },
  { to: "/studio", label: "XR Studio" },
  { to: "/distribution", label: "Distribution" },
  {
    label: "For Partners",
    children: [
      { to: "/creators", label: "For XR Creators" },
      { to: "/venues", label: "For Venues & Investors" },
    ],
  },
  { to: "/locations", label: "Locations" },
  { to: "/about", label: "About" },
  { to: "/insights", label: "Insights" },
  { to: "/contact", label: "Contact" },
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <>
      <header
        data-testid="main-nav"
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${scrolled ? "backdrop-blur-2xl bg-[#030305]/75 border-b border-white/5" : "bg-transparent"}`}
      >
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="flex items-baseline gap-1">
            <span className="font-serif text-2xl tracking-[0.08em] font-medium">VROVA</span>
            <span className="w-1.5 h-1.5 rounded-full bg-white/70 mb-0.5" />
          </Link>
          <nav className="hidden xl:flex items-center gap-7">
            {LINKS.map((l) =>
              l.children ? (
                <div key={l.label} className="relative group">
                  <button data-testid="nav-partners-dropdown" className="flex items-center gap-1 text-[13px] font-normal tracking-wide text-zinc-300 hover:text-white transition-colors duration-300">
                    {l.label} <ChevronDown size={13} />
                  </button>
                  <div className="absolute top-full left-1/2 -translate-x-1/2 pt-4 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300">
                    <div className="rounded-xl border border-white/10 backdrop-blur-2xl bg-[#0a0a0e]/95 p-2 min-w-[220px] shadow-2xl">
                      {l.children.map((c) => (
                        <NavLink key={c.to} to={c.to} data-testid={`nav-${c.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                          className="block px-4 py-2.5 rounded-lg text-[13px] text-zinc-300 hover:text-white hover:bg-white/5 transition-colors">
                          {c.label}
                        </NavLink>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <NavLink key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className={({ isActive }) => `text-[13px] font-normal tracking-wide transition-colors duration-300 ${isActive ? "text-white" : "text-zinc-400 hover:text-white"}`}>
                  {l.label}
                </NavLink>
              )
            )}
          </nav>
          <div className="flex items-center gap-4">
            <Link to="/creators" data-testid="nav-cta-partner"
              className="hidden md:inline-flex px-6 py-2.5 rounded-full bg-white text-black text-[13px] font-medium hover:bg-zinc-200 transition-colors duration-300">
              Partner With Us
            </Link>
            <button data-testid="nav-mobile-toggle" onClick={() => setOpen(!open)} className="xl:hidden p-2 text-zinc-300" aria-label="Menu">
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </header>
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-40 bg-[#030305]/97 backdrop-blur-xl flex flex-col justify-center px-10"
          >
            {[...LINKS.flatMap((l) => (l.children ? l.children : [l])), ].map((l, i) => (
              <motion.div key={l.to} initial={{ y: 24, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.06 * i, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}>
                <NavLink to={l.to} data-testid={`mobile-nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="block font-serif text-3xl py-2.5 text-zinc-300 hover:text-white transition-colors">
                  {l.label}
                </NavLink>
              </motion.div>
            ))}
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }}>
              <Link to="/creators" data-testid="mobile-nav-cta" className="inline-flex mt-8 px-8 py-3 rounded-full bg-white text-black text-sm font-medium">
                Partner With Us
              </Link>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
